/**
 * 収穫残高履歴年タブ管理
 *
 * === Public ===
 * initializeHarvestHistoryTabs        - 履歴タブの初期化（connect時に呼び出し）
 * selectHarvestHistoryYear            - タブクリック時のハンドラー
 * harvestHistoryYearsValueChanged     - Turbo Stream更新後のタブ再描画
 *
 * === Private ===
 * updateHarvestHistoryFrameSrc        - Turbo Frameのsrcを設定し、コンテンツを自動再読み込みさせる
 * setHarvestHistoryViewState          - データ/空コンテナの表示切り替え
 * renderHarvestHistoryTabs            - タブのDOM生成（テンプレートクローン方式）
 * updateHarvestHistoryTabStyles       - 選択状態のスタイル更新
 */

export const HarvestHistoryTabsManagementMixin = {
  // === Public ===

  /**
   * 履歴タブの初期化
   * connect() から呼び出される
   */
  initializeHarvestHistoryTabs() {
    const years = this.harvestHistoryYearsValue || [];

    // No years - show empty container
    if (years.length === 0) {
      this.setHarvestHistoryViewState("empty");
      this._harvestHistoryTabsInitialized = true;
      return;
    }

    // Set default selected year if not set
    if (!this.selectedHarvestHistoryYearValue) {
      this.selectedHarvestHistoryYearValue = years[0];
    }

    // Render tabs and set frame src
    this.renderHarvestHistoryTabs();
    this.updateHarvestHistoryFrameSrc(this.selectedHarvestHistoryYearValue);
    this.setHarvestHistoryViewState("data");
    this._harvestHistoryTabsInitialized = true;
  },

  /**
   * タブクリック時のハンドラー
   * data-action="click->harvest-warehouse#selectHarvestHistoryYear"
   */
  selectHarvestHistoryYear(event) {
    const year = parseInt(event.currentTarget.dataset.year, 10);
    if (year === this.selectedHarvestHistoryYearValue) return;

    this.selectedHarvestHistoryYearValue = year;
    this.updateHarvestHistoryTabStyles(year);
    this.updateHarvestHistoryFrameSrc(year);
  },

  /**
   * Turbo Streamで年リストが更新された時のコールバック
   * 出金申請の送信/キャンセルで発火（例: 今年初の履歴追加、唯一の履歴キャンセルによる年の増減）
   */
  harvestHistoryYearsValueChanged() {
    // Skip the connect-time invocation (initializeHarvestHistoryTabs handles it)
    if (!this._harvestHistoryTabsInitialized) return;

    const years = this.harvestHistoryYearsValue || [];

    if (years.length === 0) {
      this.setHarvestHistoryViewState("empty");
      return;
    }

    // If the selected year was removed, fall back to the most recent year
    // (the selected year changes, so the history frame must be reloaded)
    if (!years.includes(this.selectedHarvestHistoryYearValue)) {
      this.selectedHarvestHistoryYearValue = years[0];
      this.updateHarvestHistoryFrameSrc(this.selectedHarvestHistoryYearValue);
    }

    this.renderHarvestHistoryTabs();
    this.setHarvestHistoryViewState("data");
  },

  // === Private ===

  /**
   * Turbo Frameのsrcを設定し、コンテンツを自動再読み込みさせる
   * 呼び出し元: initializeHarvestHistoryTabs, selectHarvestHistoryYear, harvestHistoryYearsValueChanged
   * @param {number} year - 表示する年
   */
  updateHarvestHistoryFrameSrc(year) {
    if (!this.hasHarvestHistoryFrameTarget || !this.harvestHistoryPathValue) return;

    const params = new URLSearchParams({ year });
    this.harvestHistoryFrameTarget.src = `${this.harvestHistoryPathValue}?${params}`;
  },

  /**
   * データ有無に応じてdata-container/empty-containerを切り替え
   * 呼び出し元: initializeHarvestHistoryTabs, harvestHistoryYearsValueChanged
   * @param {string} state - "data" または "empty"
   */
  setHarvestHistoryViewState(state) {
    const showData = state === "data";

    if (this.hasHarvestHistoryDataContainerTarget) {
      this.harvestHistoryDataContainerTarget.style.display = showData ? "" : "none";
    }
    if (this.hasHarvestHistoryEmptyContainerTarget) {
      this.harvestHistoryEmptyContainerTarget.style.display = showData ? "none" : "";
    }
  },

  /**
   * タブのDOM生成（テンプレートクローン方式）
   * 呼び出し元: initializeHarvestHistoryTabs, harvestHistoryYearsValueChanged
   */
  renderHarvestHistoryTabs() {
    const years = this.harvestHistoryYearsValue || [];

    if (
      !this.hasHarvestHistoryTabTemplateTarget ||
      !this.hasHarvestHistoryTabsListTarget
    )
      return;

    const template = this.harvestHistoryTabTemplateTarget;
    const tabsContainer = this.harvestHistoryTabsListTarget;

    // Clear existing tabs
    tabsContainer.innerHTML = "";

    // Clone template for each year
    years.forEach((year) => {
      const tab = template.content.firstElementChild.cloneNode(true);
      tab.textContent = year;
      tab.dataset.year = year;

      if (year === this.selectedHarvestHistoryYearValue) {
        tab.setAttribute("data-selected", "");
        requestAnimationFrame(() => {
          tab.scrollIntoView({
            behavior: "smooth",
            inline: "center",
            block: "nearest",
          });
        });
      }

      tabsContainer.appendChild(tab);
    });
  },

  /**
   * 選択状態のスタイル更新
   * 呼び出し元: selectHarvestHistoryYear
   * @param {number} selectedYear - 選択された年
   */
  updateHarvestHistoryTabStyles(selectedYear) {
    this.harvestHistoryTabTargets.forEach((tab) => {
      const year = parseInt(tab.dataset.year, 10);
      tab.toggleAttribute("data-selected", year === selectedYear);
    });
  },
};
